// Known intrinsic dimensions reserve the exact aspect ratio before loading.
// Older unsized figures stay eager so their measured page geometry is unchanged.
export const handoutImageLoadProps = (asset = {}) => {
  const width = Number(asset?.width_px);
  const height = Number(asset?.height_px);
  return { decoding: "async", ...(Number.isInteger(width) && width > 0 && Number.isInteger(height) && height > 0
    ? { width, height, loading: "lazy" } : {}) };
};

export const getHandoutFocusSubsections = (source = "") => {
  const text = String(source);
  const headings = [...text.matchAll(/^### (.+)$/gm)];
  if (!headings.length) return [{ title: "觀念內容", source: text }];
  return headings.map((heading, index) => ({
    title: heading[1].trim(),
    source: text.slice(index ? heading.index : 0, headings[index + 1]?.index ?? text.length).trim(),
  }));
};

const getAnchorSection = (image) => {
  const value = image.anchorSection ?? image.anchor_section;
  return value !== undefined && value !== null && value !== "" && ["string", "number"].includes(typeof value) && Number.isInteger(Number(value)) && Number(value) >= 0
    ? Number(value) : undefined;
};

// Focus figures belong to their concept; image IDs and asset snapshots stay intact.
export const getHandoutFocusImages = (section = {}) => (Array.isArray(section.images) ? section.images : [])
  .filter((image) => image && typeof image === "object" &&
    ((image.asset && typeof image.asset === "object") || typeof image.url === "string"))
  .map((image) => ({ ...image, asset: image.asset || { id: image.id, url: image.url } }))
  .map((image, index) => ({
    ...image,
    id: String(image.id || image.asset.id || `${section.id || "focus"}-image-${index}`),
    ...(getAnchorSection(image) !== undefined ? { anchorSection: getAnchorSection(image) } : {}),
    widthCm: Math.min(16, Math.max(2, Number(image.widthCm ?? image.width_cm) || 4)),
    alignment: ["left", "center", "right"].includes(image.alignment) ? image.alignment : "right",
    layout: image.layout === "block" || image.alignment === "center" ? "block" : "float",
  }));

export const serializeHandoutFocusImages = (section) => getHandoutFocusImages(section).map((image) => ({
  id: image.id,
  url: image.asset._preview_url || image.asset._previewUrl || image.asset.url || "",
  ...(image.asset.source_language === "python" && image.asset.source_code ? { asset: { source_language: "python", source_code: image.asset.source_code } } : {}),
  width_cm: image.widthCm,
  alignment: image.alignment,
  layout: image.layout,
  ...(getAnchorSection(image) !== undefined ? { anchor_section: getAnchorSection(image) } : {}),
}));

export const moveImageSectionIntoFocus = (sections, imageId, focusId) => {
  const source = sections.find((section) => section.id === imageId && section.type === "image");
  const target = sections.find((section) => section.id === focusId && section.type === "focus");
  if (!source || !target || !source.asset) return sections;
  const alignment = ["left", "center", "right"].includes(source.alignment) ? source.alignment : "right";
  const widthCm = source.widthCm || 4;
  const layout = alignment === "center" ? "block" : ["float", "block"].includes(source.layout)
    ? source.layout : Number(widthCm) > 6 ? "block" : "float";
  const image = { id: source.id, asset: { ...source.asset }, widthCm, alignment, layout,
    ...(source.backgroundRemoval ? { backgroundRemoval: source.backgroundRemoval } : {}),
  };
  return sections.filter((section) => section.id !== imageId).map((section) => {
    if (section.id !== focusId) return section;
    const images = getHandoutFocusImages(section);
    const result = { ...section, images: images.some((existing) => existing.id === image.id) ? images : [...images, image] };
    if (String(source.text || "").trim()) {
      const content = section.markdown ?? section.lines ?? section.focusLines ?? "";
      result.lines = result.markdown = [Array.isArray(content) ? content.join("\n") : content, source.text]
        .filter(Boolean).join("\n\n");
    }
    return result;
  });
};
